import React, { Key, useEffect, useRef, useState, useCallback } from 'react';
import { AudioOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { PaperAirplaneIcon } from '@heroicons/react/24/solid';
import { v4 as uuid } from 'uuid';
import AgentAnswer, { getAgentAnswerParts, hasAgentAnswer } from '../agentAnswer';
import styles from './index.module.scss';
import { Role } from '@/pages/chat/type';
import { EventSourcePolyfill, MessageEventLike } from '../../util';
import { getConversationList, getMessagesList } from '@/api/chat';
import type { ConversationRespVo } from '@/api/chat';
import { message, Spin } from 'antd';
import { useChatContext } from '@/pages/chat/context';
import type { AssistantPanelType } from '@/pages/chat/context';
import { ContentType } from './util';
import type { MessagePart, StructuredOutput } from './util';
import { buildHistoryMessages } from './historyProcessor';
import icon from '@/assets/images/dark-icon-agent@2x.png';
import dotLoading from '@/assets/images/dotLoading@2x.png';
import { useThemeImage } from '@/hooks/useThemeImage';

import {
  applyReasoningContentToRound,
  buildAssistantParts,
  cacheToolResultOutputs,
  clearRoundStatuses,
  collectReasoningContent,
  createStreamState,
  getEventMessageId,
  getEventType,
  getReasoningEventState,
  getThinkingTime,
  updateCurrentRoundStatus,
} from './sseEvent';
import type { StreamState } from './sseEvent';
import { formatRecordingDuration, RECORDING_WAVE_BAR_COUNT, useVoiceRecognition } from './useVoiceRecognition';

interface ChatInterfaceProps {
  active: boolean;
  title: string;
  panelType?: AssistantPanelType;
  recommendedQuestion?: string;
  onRecommendedQuestionConsumed?: () => unknown;
}

export type { MessagePart, StructuredOutput };
export { ContentType };

export interface Message {
  agentCode?: string;
  enableThinking?: number;
  structuredOutputs?: StructuredOutput[];
  answerFinished?: boolean;
  role?: Role;
  parts?: MessagePart[];
  replying?: boolean;
  id: Key;
}

interface ConversationState {
  messages: Message[];
  isReplying: boolean;
  currentMsgId: string | null;
}

interface ConversationMessagesStore {
  [key: string]: ConversationState;
}

interface SuggestedQuestionsStore {
  [key: string]: string[];
}

const STREAM_RENDER_INTERVAL = 80;
const AUTO_SCROLL_THRESHOLD = 120;
const DONE_EVENT_TYPES = new Set(['DONE', 'FINISH', 'FINISHED', 'COMPLETE', 'COMPLETED']);
const EMPTY_STREAM_ERROR_MESSAGE = '本次没有收到有效回复，可能是网络波动或响应提前结束，请重试';
const EFFECTIVE_REPLY_PART_TYPES = new Set<MessagePart['type']>([
  'text',
  'markdown',
  'chart',
  'table',
  'excel',
  'word',
  'pdf',
  'file',
  'image',
]);

const isDoneEvent = (eventType?: string, data?: any) => {
  const normalizedEventType = String(eventType || '')
    .trim()
    .toUpperCase();
  if (DONE_EVENT_TYPES.has(normalizedEventType)) {
    return true;
  }

  if (typeof data === 'string') {
    const normalizedData = data.trim().toUpperCase();
    return normalizedData === '[DONE]' || DONE_EVENT_TYPES.has(normalizedData);
  }

  const normalizedDataType = String(data?.type ?? data?.eventType ?? data?.event ?? data?.status ?? '')
    .trim()
    .toUpperCase();
  return DONE_EVENT_TYPES.has(normalizedDataType);
};

const hasEffectiveReplyParts = (parts: MessagePart[]) =>
  parts.some(part => {
    if (!EFFECTIVE_REPLY_PART_TYPES.has(part.type)) {
      return false;
    }

    if (part.type === 'text' || part.type === 'markdown') {
      return Boolean(String(part.data ?? '').trim());
    }

    return part.data !== undefined && part.data !== null;
  });

const getSuggestedQuestions = (payload: any) => {
  const questions = payload?.questions ?? payload?.suggestedQuestions;
  if (!Array.isArray(questions)) return [];

  return Array.from(
    new Set(
      questions
        .filter((question): question is string => typeof question === 'string')
        .map(question => question.trim())
        .filter(Boolean),
    ),
  );
};

const getConversationName = (payload: any) => {
  const candidates = [
    payload?.conversationName,
    payload?.data?.conversationName,
    payload?.data?.data?.conversationName,
    payload?.result?.conversationName,
    payload?.metadata?.conversationName,
  ];
  const name = candidates.find(candidate => typeof candidate === 'string' && candidate.trim());
  return typeof name === 'string' ? name.trim() : undefined;
};

const hasActualConversationName = (name?: string) => {
  const normalizedName = name?.trim();
  return Boolean(normalizedName && normalizedName !== '新对话' && normalizedName !== '未命名对话');
};

const conversationListUpdatedEvent = 'assistant-conversation-list-updated';

const notifyConversationListUpdated = (conversationResp: ConversationRespVo) => {
  window.dispatchEvent(new CustomEvent(conversationListUpdatedEvent, { detail: conversationResp }));
};

const ChatInterface = ({
  active,
  title,
  panelType,
  recommendedQuestion,
  onRecommendedQuestionConsumed,
}: ChatInterfaceProps) => {
  const {
    history,
    conversation,
    isTalking,
    setTalking,
    setConversation,
    setHistory,
    config,
    processInfo,
    openRequest,
    setOpenRequest,
  } = useChatContext();
  const [messageApi, messageContextHolder] = message.useMessage();
  const { tenantMcid, staffId, hasHistory = true } = config || {};
  const thinkingIcon = useThemeImage('loading');
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestedQuestionsStore, setSuggestedQuestionsStore] = useState<SuggestedQuestionsStore>({});
  const pendingSuggestedQuestionsRef = useRef<Map<string, string[]>>(new Map());
  const suggestedQuestionsAwaitingRenderRef = useRef<Map<string, { msgId: string; content: string }>>(new Map());
  const completedTextRenderRef = useRef<Map<string, string>>(new Map());
  const composingRef = useRef(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const handleSendRef = useRef<((message?: string, source?: 'text' | 'voice') => Promise<boolean>) | undefined>(
    undefined,
  );
  const {
    cancelTranscription,
    isVoiceBusy,
    cancelRecording: handleCancelRecording,
    confirmRecording: handleConfirmRecording,
    isAsrEnabled,
    isRecording,
    isTranscribing,
    recordingSeconds,
    startRecording: handleRecordClick,
    waveformRef,
  } = useVoiceRecognition({
    active,
    messageApi,
    onRecognized: recognizedText => handleSendRef.current?.(recognizedText, 'voice') ?? false,
  });

  const [conversationsStore, setConversationsStore] = useState<ConversationMessagesStore>({});
  const currentDisplayId = history?.id || conversation?.id || 'default';

  // 获取当前会话的消息
  const currentConversation = conversationsStore[currentDisplayId] || {
    messages: [],
    isReplying: false,
    currentMsgId: null,
  };
  const messages = currentConversation.messages;
  const isCurrentReplying = currentConversation.isReplying;
  const currentSuggestedQuestions = suggestedQuestionsStore[currentDisplayId] ?? [];

  // SSE 连接存储（按 msgId 存储，但关联 conversationId）
  const eventSourcesRef = useRef<Map<string, { eventSource: EventSourcePolyfill }>>(new Map());
  // 每条消息的 streamState（按 msgId）
  const streamStatesRef = useRef<Map<string, StreamState>>(new Map());
  // 正在回复的会话 ID 集合（用于判断是否需要加载历史）
  const replyingConversationsRef = useRef<Set<string>>(new Set());
  const streamRenderTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const scrollFrameRef = useRef<number | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);

  const revealPendingSuggestedQuestions = useCallback((convId: string) => {
    const questions = pendingSuggestedQuestionsRef.current.get(convId);
    if (questions?.length) {
      setSuggestedQuestionsStore(prev => ({ ...prev, [convId]: questions }));
    }

    const awaitingRender = suggestedQuestionsAwaitingRenderRef.current.get(convId);
    if (awaitingRender) completedTextRenderRef.current.delete(awaitingRender.msgId);
    suggestedQuestionsAwaitingRenderRef.current.delete(convId);
    pendingSuggestedQuestionsRef.current.delete(convId);
  }, []);

  const handleReplyRenderComplete = useCallback(
    (convId: string, msgId: string, content: string) => {
      const isCurrentStream = eventSourcesRef.current.has(msgId);
      const awaitingRender = suggestedQuestionsAwaitingRenderRef.current.get(convId);
      const isAwaitingRender = awaitingRender?.msgId === msgId;
      if (!isCurrentStream && !isAwaitingRender) return;

      completedTextRenderRef.current.set(msgId, content);
      if (isAwaitingRender && awaitingRender.content === content) {
        revealPendingSuggestedQuestions(convId);
      }
    },
    [revealPendingSuggestedQuestions],
  );

  const applyConversationName = useCallback(
    (conversationId: string, conversationName?: string) => {
      if (!conversationName) return;

      setConversation?.(current =>
        current?.id === conversationId && current.name !== conversationName
          ? { ...current, name: conversationName }
          : current,
      );
      setHistory?.(current =>
        current?.id === conversationId && current.name !== conversationName
          ? { ...current, name: conversationName }
          : current,
      );
    },
    [setConversation, setHistory],
  );

  const refreshConversationName = useCallback(
    (conversationId: string) => {
      if (!tenantMcid || !staffId) return;

      getConversationList({ tenantMcid, staffId })
        .then(response => {
          notifyConversationListUpdated(response);
          const currentConversation = response.records?.find(item => item.conversationId === conversationId);
          applyConversationName(conversationId, currentConversation?.conversationName);
        })
        .catch(error => {
          console.error('Failed to refresh conversation name:', error);
        });
    },
    [applyConversationName, staffId, tenantMcid],
  );

  // 更新指定会话的消息
  const updateConversationMessages = useCallback(
    (convId: string, newMessages: Message[], isReplying?: boolean, currentMsgId?: string | null) => {
      const snapshots = newMessages.map(msg => {
        const state = streamStatesRef.current.get(String(msg.id));
        return state ? { ...msg, structuredOutputs: Array.from(state.structuredOutputsCache.values()), answerFinished: state.replyFinished } : msg;
      });
      setConversationsStore(prev => {
        const existing = prev[convId] || {
          messages: [],
          isReplying: false,
          currentMsgId: null,
        };
        // 用 Map 做索引，方便快速查找
        const msgMap = new Map(existing.messages.map(msg => [msg.id, msg]));

        snapshots.forEach(newMsg => {
          const existingMsg = msgMap.get(newMsg.id);
          if (existingMsg) {
            msgMap.set(newMsg.id, {
              ...existingMsg,
              ...newMsg,
              replying: newMsg.replying ?? existingMsg.replying,
              parts: newMsg.parts !== undefined ? [...newMsg.parts] : existingMsg.parts,
            });
          } else {
            msgMap.set(newMsg.id, newMsg);
          }
        });

        // 更新正在回复的会话集合
        if (isReplying !== undefined) {
          if (isReplying) {
            replyingConversationsRef.current.add(convId);
          } else {
            replyingConversationsRef.current.delete(convId);
          }
        }

        return {
          ...prev,
          [convId]: {
            messages: Array.from(msgMap.values()),
            isReplying: isReplying !== undefined ? isReplying : existing.isReplying,
            currentMsgId: currentMsgId !== undefined ? currentMsgId : existing.currentMsgId,
          },
        };
      });
    },
    [],
  );

  const setConversationMessages = useCallback((convId: string, messages: Message[]) => {
    setConversationsStore(prev => ({
      ...prev,
      [convId]: {
        messages,
        isReplying: false,
        currentMsgId: null,
      },
    }));
  }, []);

  const isNearBottom = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight <= AUTO_SCROLL_THRESHOLD;
  }, []);

  const scrollToBottom = useCallback(
    (force = false) => {
      const el = scrollRef.current;
      if (!el || (!force && !isNearBottom())) {
        return;
      }

      if (scrollFrameRef.current !== null) {
        cancelAnimationFrame(scrollFrameRef.current);
      }

      scrollFrameRef.current = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const nextEl = scrollRef.current;
          if (!nextEl) return;
          nextEl.scrollTop = nextEl.scrollHeight;
          scrollFrameRef.current = null;
        });
      });
    },
    [isNearBottom],
  );

  const handleTypingProgress = useCallback(() => {
    scrollToBottom();
  }, [scrollToBottom]);

  const buildVisibleAssistantParts = buildAssistantParts;

  const flushStreamRender = useCallback(
    (msgId: string, convId: string, shouldScroll: boolean) => {
      streamRenderTimersRef.current.delete(msgId);
      const streamState = streamStatesRef.current.get(msgId);
      if (!streamState) return;

      updateConversationMessages(convId, [
        { id: msgId, parts: buildVisibleAssistantParts(streamState), replying: !streamState.replyFinished },
      ]);

      if (shouldScroll) {
        scrollToBottom(true);
      }
    },
    [buildVisibleAssistantParts, scrollToBottom, updateConversationMessages],
  );

  const scheduleStreamRender = useCallback(
    (msgId: string, convId: string) => {
      if (streamRenderTimersRef.current.has(msgId)) {
        return;
      }

      const shouldScroll = isNearBottom();
      const timer = setTimeout(() => {
        flushStreamRender(msgId, convId, shouldScroll);
      }, STREAM_RENDER_INTERVAL);

      streamRenderTimersRef.current.set(msgId, timer);
    },
    [flushStreamRender, isNearBottom],
  );

  const cancelPendingStreamRender = useCallback((msgId: string) => {
    const timer = streamRenderTimersRef.current.get(msgId);
    if (timer) {
      clearTimeout(timer);
      streamRenderTimersRef.current.delete(msgId);
    }
  }, []);

  const cleanupConversation = useCallback(
    (convId: string) => {
      eventSourcesRef.current.forEach((value, msgId) => {
        if (value.convId === convId) {
          value.eventSource.close();
          eventSourcesRef.current.delete(msgId);
          streamStatesRef.current.delete(msgId);
          cancelPendingStreamRender(msgId);
        }
      });
    },
    [cancelPendingStreamRender],
  );

  // 初始化或开始新对话
  useEffect(() => {
    if (!isTalking) {
      if (conversation?.id) {
        cleanupConversation(conversation.id);
        setConversationMessages(conversation.id, []);
      } else {
        cleanupConversation('default');
        setConversationMessages('default', []);
      }
      setConversationsStore(prev => {
        const id = conversation?.id || 'default';
        return {
          ...prev,
          [id]: {
            messages: [],
            isReplying: false,
            currentMsgId: null,
          },
        };
      });
    }
  }, [isTalking, conversation, cleanupConversation, setConversationMessages]);

  // 组件卸载时清理所有资源
  useEffect(() => {
    return () => {
      eventSourcesRef.current.forEach(({ eventSource }) => eventSource.close());
      eventSourcesRef.current.clear();
      streamStatesRef.current.clear();
      pendingSuggestedQuestionsRef.current.clear();
      suggestedQuestionsAwaitingRenderRef.current.clear();
      completedTextRenderRef.current.clear();
      streamRenderTimersRef.current.forEach(timer => clearTimeout(timer));
      streamRenderTimersRef.current.clear();
      if (scrollFrameRef.current !== null) {
        cancelAnimationFrame(scrollFrameRef.current);
      }
    };
  }, []);

  // 加载历史消息
  useEffect(() => {
    if (history?.id) {
      // 不中断其他正在进行的 SSE 请求，只是切换显示
      setLoading(true);
      // 使用 ref 判断是否正在回复，避免依赖 conversationsStore
      if (replyingConversationsRef.current.has(history.id)) {
        setLoading(false);
        scrollToBottom(true);
        return;
      }

      getMessagesList({ conversationId: history.id, tenantMcid, staffId })
        .then(res => {
          const historyMessages = buildHistoryMessages((res ?? []) as any[]) as Message[];
          setConversationMessages(
            history.id,
            historyMessages.map(message =>
              message.role === Role.ASSISTANT && message.parts
                ? { ...message, agentCode: processInfo?.agentCode, enableThinking: processInfo?.enableThinking }
                : message,
            ),
          );
          scrollToBottom(true);
        })
        .catch(err => {
          console.error('加载历史消息失败:', err);
          setConversationMessages(history.id, []);
        })
        .finally(() => setLoading(false));
    }
  }, [processInfo?.agentCode, processInfo?.enableThinking, history, tenantMcid, staffId, setConversationMessages, scrollToBottom]);

  const handleReasoningEvent = (data: any, msgId: string, streamState: StreamState, convId: string) => {
    const reasoningContent = collectReasoningContent(data);
    const eventState = getReasoningEventState(data, reasoningContent);
    const roundMessageId = getEventMessageId(data, msgId);
    const thinkingTime = getThinkingTime(data);
    applyReasoningContentToRound(streamState, roundMessageId, reasoningContent, eventState, thinkingTime);

    // 会话是否结束只看 done，不以 last 作为结束信号
    scheduleStreamRender(msgId, convId);
  };

  const handleToolResultEvent = (data: any, msgId: string, streamState: StreamState, convId: string) => {
    cacheToolResultOutputs(data, streamState.structuredOutputsCache);

    // 渲染当前内容（TOOL_RESULT 收到时也走节流，避免图表/Markdown 反复重排）
    scheduleStreamRender(msgId, convId);
  };

  const handleToolUseEvent = (msgId: string, streamState: StreamState, convId: string) => {
    // 如果当前内容为空，保持默认 loading 效果
    updateCurrentRoundStatus(streamState, streamState.statusContent || '正在思考...');
    scheduleStreamRender(msgId, convId);
  };

  /**
   * 发送消息的异步处理函数
   * 负责处理用户输入的消息，生成对应的AI回复，并实时更新到消息列表中
   */
  const handleSend = async (message = input, source: 'text' | 'voice' = 'text'): Promise<boolean> => {
    if (source !== 'voice' && isVoiceBusy()) return false;
    if (!message.trim()) return false;
    if (isCurrentReplying) return false;
    if (!processInfo?.id) return false;

    const msgId = Date.now().toString();
    const curConversationId = conversation?.id ?? uuid().replace(/-/g, '').slice(0, 32);
    const actualConversationId = history?.id || curConversationId;
    const convId = actualConversationId;
    const currentConversationName = history?.name || conversation?.name;
    const isFirstConversation = !history && !conversation;

    if (!isFirstConversation && (history || conversation) && !hasActualConversationName(currentConversationName)) {
      refreshConversationName(actualConversationId);
    }

    const userMessage: Message = { id: msgId + '_u', role: Role.USER, parts: [{ type: 'text', data: message }] };
    const aiThinking: Message = { id: msgId, role: Role.ASSISTANT, parts: [], replying: true, agentCode: processInfo.agentCode, enableThinking: processInfo.enableThinking };

    setSuggestedQuestionsStore(prev => {
      if (!prev[convId]) return prev;
      const next = { ...prev };
      delete next[convId];
      return next;
    });
    pendingSuggestedQuestionsRef.current.delete(convId);
    const awaitingRender = suggestedQuestionsAwaitingRenderRef.current.get(convId);
    if (awaitingRender) completedTextRenderRef.current.delete(awaitingRender.msgId);
    suggestedQuestionsAwaitingRenderRef.current.delete(convId);

    setTalking?.(true);
    setInput('');
    setLoading(true);
    textareaRef.current?.blur();

    // 新会话时设置 conversation 对象
    if (!history && !conversation) {
      setConversation?.({
        id: actualConversationId,
        name: '新对话',
        type: processInfo?.agentName || '新对话',
      });
    }

    updateConversationMessages(convId, [userMessage, aiThinking], true, msgId);
    scrollToBottom(true);

    const winConfig = window.assistantConfig ?? {};
    const apiBase = winConfig.apiUrl || winConfig.baseUrl || 'https://assistant.test.maxtropy.com';

    try {
      let eventSource: EventSourcePolyfill;
      const streamState = createStreamState({ statusContent: '正在思考...' });
      streamStatesRef.current.set(msgId, streamState);

      eventSource = new EventSourcePolyfill(
        `${apiBase}/api/agent/react/streamByAgentConfig?tenantMcid=${encodeURIComponent(tenantMcid || '')}&staffId=${encodeURIComponent(staffId || '')}&conversationId=${encodeURIComponent(actualConversationId)}&message=${encodeURIComponent(message)}&agentConfigId=${processInfo?.id || ''}&recordHistory=${processInfo?.recordHistory !== 0}&cookie=GT=${encodeURIComponent(winConfig.GT || '')}`,
        { method: 'GET' },
      );

      eventSourcesRef.current.set(msgId, { eventSource, convId });

      const finalizeReply = (options?: { errorMessage?: string }) => {
        const currentState = streamStatesRef.current.get(msgId);
        if (!currentState) return;

        const alreadyFinished = currentState.replyFinished;
        currentState.replyFinished = true;
        cancelPendingStreamRender(msgId);
        clearRoundStatuses(currentState);

        const finalParts = buildVisibleAssistantParts(currentState);
        const finalAnswer: Message = { ...aiThinking, parts: finalParts, answerFinished: true, structuredOutputs: Array.from(currentState.structuredOutputsCache.values()) };
        const hasEffectiveContent = hasEffectiveReplyParts(getAgentAnswerParts(finalAnswer)) || hasAgentAnswer(finalAnswer);

        if (options?.errorMessage && !alreadyFinished) {
          const parts = finalParts.length
            ? [...finalParts, { type: 'error' as const, data: options.errorMessage }]
            : [{ type: 'error' as const, data: options.errorMessage }];

          updateConversationMessages(convId, [{ id: msgId, parts, replying: false }], false, null);
        } else if (!hasEffectiveContent) {
          updateConversationMessages(
            convId,
            [{ id: msgId, parts: [{ type: 'error' as const, data: EMPTY_STREAM_ERROR_MESSAGE }], replying: false }],
            false,
            null,
          );
        } else {
          updateConversationMessages(convId, [{ id: msgId, parts: finalParts, replying: false }], false, null);
        }

        const suggestedQuestions = pendingSuggestedQuestionsRef.current.get(convId);
        const canShowSuggestedQuestions = suggestedQuestions?.length && hasEffectiveContent && !options?.errorMessage;
        if (canShowSuggestedQuestions) {
          const finalTextPart = [...getAgentAnswerParts(finalAnswer)]
            .reverse()
            .find(part => part.type === 'text' || part.type === 'markdown');
          if (finalTextPart) {
            const finalTextContent = String(finalTextPart.data ?? '');
            suggestedQuestionsAwaitingRenderRef.current.set(convId, { msgId, content: finalTextContent });
            if (completedTextRenderRef.current.get(msgId) === finalTextContent) {
              revealPendingSuggestedQuestions(convId);
            }
          } else {
            revealPendingSuggestedQuestions(convId);
          }
        } else {
          pendingSuggestedQuestionsRef.current.delete(convId);
        }
        scrollToBottom(true);

        eventSource.close();
        eventSourcesRef.current.delete(msgId);
        streamStatesRef.current.delete(msgId);
        setLoading(false);

        if (isFirstConversation) {
          refreshConversationName(convId);
        }
      };

      eventSource.onmessage = (event: MessageEventLike) => {
        setLoading(false);

        const eventType = event.type;
        const dataStr = event.data;

        if (isDoneEvent(eventType, dataStr)) {
          finalizeReply();
          return;
        }

        try {
          const parsed = JSON.parse(dataStr);
          const parsedEventType = getEventType(parsed, eventType);
          applyConversationName(convId, getConversationName(parsed));
          console.log('SSE Event:', parsedEventType, parsed);

          if (isDoneEvent(parsedEventType, parsed)) {
            finalizeReply();
            return;
          }

          if (parsedEventType === 'SUGGESTED_QUESTIONS') {
            const questions = getSuggestedQuestions(parsed);
            pendingSuggestedQuestionsRef.current.set(convId, questions);
            return;
          }

          const currentState = streamStatesRef.current.get(msgId);
          if (!currentState) return;

          if (parsedEventType === 'REASONING') {
            handleReasoningEvent(parsed, msgId, currentState, convId);
          } else if (parsedEventType === 'TOOL_RESULT') {
            handleToolResultEvent(parsed, msgId, currentState, convId);
          } else if (parsedEventType === 'TOOL_USE') {
            handleToolUseEvent(msgId, currentState, convId);
          } else if (parsedEventType === 'ERROR' || parsed?.type === 'error') {
            finalizeReply({ errorMessage: parsed?.msg || parsed?.message || parsed?.data || '流式请求失败' });
          }
        } catch (e) {
          console.error('Parse SSE data error:', e);
          updateConversationMessages(
            convId,
            [{ id: msgId, parts: [{ type: 'text', data: dataStr }], replying: false }],
            false,
            null,
          );
          scrollToBottom(true);
        }
      };

      // 注意：SSE 流正常结束时也会触发 onerror，需要判断 readyState
      // readyState: CONNECTING=0, OPEN=1, CLOSED=2
      eventSource.onerror = err => {
        if (eventSource.readyState === EventSource.CLOSED) {
          // 流正常结束
          console.log('SSE stream completed normally');
          finalizeReply({ content: accumulatedContent });
        } else {
          // 真正的错误
          console.error('SSE Error:', err);
          const errorMessage = err?.message || '连接中断，请稍后重试';
          finalizeReply({ errorMessage });
        }
      };
    } catch (err) {
      console.error('Error:', err);
      setLoading(false);
      eventSourcesRef.current.delete(msgId);
      streamStatesRef.current.delete(msgId);
      return false;
    }
    return true;
  };

  handleSendRef.current = handleSend;

  useEffect(() => {
    const question = recommendedQuestion?.trim();
    if (!question) return;

    onRecommendedQuestionConsumed?.();
    handleSendRef.current?.(question).catch(error => console.error('发送推荐问题失败:', error));
  }, [onRecommendedQuestionConsumed, recommendedQuestion]);

  useEffect(() => {
    if (openRequest?.type !== 'navigation') return;

    const query = openRequest.params?.query;
    if (typeof query !== 'string' || !query.trim()) {
      setOpenRequest?.(undefined);
      return;
    }
    if (!processInfo?.id || !tenantMcid || !staffId || isCurrentReplying) return;

    setOpenRequest?.(undefined);
    handleSendRef.current?.(query.trim()).catch(error => console.error('发送导航问题失败:', error));
  }, [isCurrentReplying, openRequest, processInfo?.id, setOpenRequest, staffId, tenantMcid]);

  const handleStop = () => {
    const convId = currentDisplayId;
    const conv = conversationsStore[convId];
    if (!conv || !conv.currentMsgId) return;

    const msgId = conv.currentMsgId;
    const esEntry = eventSourcesRef.current.get(msgId);
    if (esEntry) {
      esEntry.eventSource.close();
      eventSourcesRef.current.delete(msgId);
    }

    cancelPendingStreamRender(msgId);
    const currentState = streamStatesRef.current.get(msgId);
    updateConversationMessages(
      convId,
      [{ id: msgId, parts: currentState ? buildVisibleAssistantParts(currentState) : undefined, replying: false }],
      false,
      null,
    );
    streamStatesRef.current.delete(msgId);
    setLoading(false);
  };

  const renderMessage = (msg: Message) => {
    const showThinking = msg.enableThinking !== 0;
    const isAssistantThinking = msg.role === Role.ASSISTANT && msg.replying;
    const hasVisibleParts = Boolean(
      hasAgentAnswer(msg) || getAgentAnswerParts(msg).some(part => showThinking || !['thinkingBlock', 'thinkingTimeline', 'toolStatus'].includes(part.type)),
    );
    const activeReplyMsgId = currentConversation.currentMsgId;
    const isCurrentReplyMessage = activeReplyMsgId === msg.id;
    const hasOtherActiveReply = Boolean(activeReplyMsgId && !isCurrentReplyMessage);

    return (
      <React.Fragment key={msg.id}>
        {isAssistantThinking && (
          <div className={styles.thinkingStatus}>
            <img src={thinkingIcon} alt="" className={styles.thinkingIcon} />
            <span>正在思考中</span>
          </div>
        )}
        {(hasVisibleParts || !isAssistantThinking) && (
          <AgentAnswer
            msg={msg}
            loading={loading}
            handleStop={handleStop}
            hideReplyAvatar={isAssistantThinking}
            onTyping={handleTypingProgress}
            onRenderComplete={content => handleReplyRenderComplete(currentDisplayId, String(msg.id), content)}
            onNavigationOptionClick={option => {
              handleSend(option).catch(error => console.error('发送导航选项失败:', error));
            }}
            showThinking={showThinking}
            enableTypewriter={isCurrentReplyMessage}
            forceCompleteTypewriter={hasOtherActiveReply}
          />
        )}
      </React.Fragment>
    );
  };

  return (
    <div className="flex flex-col h-full relative">
      {messageContextHolder}
      {isTalking &&
        (panelType === 'navigation' ? (
          <div className={styles.mainTitle}>
            <div className={styles.type}>
              <img src={icon} width={14} /> {history?.agentName ?? processInfo?.agentName}
            </div>
          </div>
        ) : (
          <div className={`${styles.mainTitle} ${!hasHistory ? styles.mainTitleLargeScreen : ''}`}>
            {title}
            <div className={styles.type}>
              <img src={icon} width={14} /> {history?.agentName ?? processInfo?.agentName}
            </div>
          </div>
        ))}
      {isTalking ? (
        <div
          className={`flex-1 overflow-y-auto pt-12 ${styles.messages} ${!hasHistory ? styles.messagesLargeScreen : ''}`}
          ref={scrollRef}
          style={{ height: '100%', overflowY: 'auto', position: 'relative' }}
        >
          {loading && (!messages || messages.length === 0) ? (
            <div className="h-full w-full flex items-center justify-center">
              <Spin size="small" />
            </div>
          ) : (
            <>{messages?.map(renderMessage)}</>
          )}
        </div>
      ) : null}
      {isTalking && currentSuggestedQuestions.length > 0 && (
        <div className={styles.suggestedQuestions} aria-label="建议问题">
          {currentSuggestedQuestions.map(question => (
            <button
              key={question}
              type="button"
              disabled={isCurrentReplying || isRecording || isTranscribing}
              onClick={() => {
                handleSend(question).catch(error => console.error('发送建议问题失败:', error));
              }}
            >
              {question}
            </button>
          ))}
        </div>
      )}
      <div
        className={`${styles.textareaContent} ${isTalking ? styles.textareaContentTalking : ''} ${
          !hasHistory && isTalking ? styles.textareaContentLargeScreen : ''
        }`}
      >
        <textarea
          ref={textareaRef}
          placeholder="搜索、提问或者发消息"
          value={input}
          onChange={e => setInput(e.target.value)}
          onCompositionStart={() => {
            composingRef.current = true;
          }}
          onCompositionEnd={() => {
            composingRef.current = false;
          }}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              if (composingRef.current || (e.nativeEvent as any).isComposing || (e as any).keyCode === 229) {
                return;
              }
              if (e.shiftKey) {
                return;
              } else {
                e.preventDefault();
                handleSend();
              }
            }
          }}
          className={styles.textarea}
        />
        {isRecording ? (
          <div className={styles.recordingControls} role="group" aria-label="录音控制">
            <button
              type="button"
              onClick={handleCancelRecording}
              className={styles.recordingActionButton}
              aria-label="取消录音"
              title="取消录音"
            >
              <CloseOutlined />
            </button>
            <div
              className={styles.recordingProgress}
              aria-label={`已录音 ${formatRecordingDuration(recordingSeconds)}`}
            >
              <div ref={waveformRef} className={styles.recordingWaveform} aria-hidden="true">
                {Array.from({ length: RECORDING_WAVE_BAR_COUNT }, (_, index) => (
                  <span key={index} />
                ))}
              </div>
              <span className={styles.recordingDuration}>{formatRecordingDuration(recordingSeconds)}</span>
            </div>
            <button
              type="button"
              onClick={handleConfirmRecording}
              className={styles.recordingActionButton}
              aria-label="结束录音"
              title="结束录音"
            >
              <CheckOutlined />
            </button>
          </div>
        ) : isTranscribing ? (
          <div className={styles.recordingControls} role="group" aria-label="语音识别控制">
            <button type="button" disabled className={styles.recordingActionButton} aria-label="识别中" title="识别中">
              <img src={dotLoading} className={styles.recognizingLoading} alt="识别中" />
            </button>
            <button
              type="button"
              onClick={cancelTranscription}
              className={styles.recordingActionButton}
              aria-label="取消语音识别"
              title="取消语音识别"
            >
              <CloseOutlined />
            </button>
          </div>
        ) : (
          <>
            {isAsrEnabled && (
              <button
                type="button"
                onClick={handleRecordClick}
                disabled={isCurrentReplying}
                className={styles.voiceButton}
                aria-label="开始语音输入"
                title="语音输入"
              >
                <AudioOutlined />
              </button>
            )}
            <button
              onClick={() => handleSend()}
              disabled={isCurrentReplying || !input || !processInfo?.id}
              className={styles.sendButton}
            >
              <PaperAirplaneIcon className="inline-block w-[16px] rotate-[315deg] align-[-1px]" />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default ChatInterface;
